import { useEffect, useState } from 'react';

import {
  ExplicitUserAccessFieldsFragment,
  ExplicitUserAccessType,
  useCreateExplicitUserAccessMutation,
  useDeleteExplicitUserAccessMutation,
  useListExplicitUserAccessesQuery,
  useOnCreateExplicitUserAccessSubscription,
  useOnDeleteExplicitUserAccessSubscription,
} from '@/@generated/codegen/API';
import { captureSentry } from '@/utils/helpers/sentryHelper';

const useMeetingUserAccessForMeeting = (meetingId: string | undefined) => {
  // --------------------- state ------------------------
  const [userAccessList, setUserAccessList] = useState<ExplicitUserAccessFieldsFragment[]>([]);
  const [mutationCreateExplicitUserAccess] = useCreateExplicitUserAccessMutation();
  const [mutationDeleteExplicitUserAccess] = useDeleteExplicitUserAccessMutation();
  const userMeetingAccessQuery = useListExplicitUserAccessesQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {
        meetingId: {
          equals: meetingId,
        },
      },
    },
  });

  // --------------------- handlers ------------------------

  const giveUserAccess = async (userId: string) => {
    if (!meetingId) {
      captureSentry({
        title: 'meetingId is undefined in updateUserAccess',
        detail: {
          meetingId,
          userId,
        },
      });
      return;
    }
    try {
      await mutationCreateExplicitUserAccess({
        variables: {
          data: {
            type: ExplicitUserAccessType.Meeting,
            objId: meetingId,
            userId,
            validUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // next 1 year
          },
        },
      });
    } catch (error) {
      captureSentry({
        title: 'Error creating user meeting access',
        detail: {
          meetingId,
          userId,
        },
        error,
      });
    }
  };

  const deleteAccess = async (id: string) => {
    try {
      await mutationDeleteExplicitUserAccess({
        variables: {
          where: {
            id: id,
          },
        },
      });
    } catch (error) {
      captureSentry({
        title: 'Error deleting user meeting access',
        detail: {
          meetingId,
        },
        error,
      });
    }
  };

  // --------------------- effect ------------------------

  useOnCreateExplicitUserAccessSubscription({
    variables: {
      where: {
        meetingId: {
          equals: meetingId,
        },
      },
    },
    onData: (res) => {
      const obj = res.data.data?.onCreateExplicitUserAccess;
      if (!obj) {
        captureSentry({
          title: 'useMeetingUserAccessForMeeting | onData | onCreate | obj is undefined',
          detail: {},
        });
        return;
      }
      setUserAccessList((prev) => [...prev, obj]);
    },
  });

  useOnDeleteExplicitUserAccessSubscription({
    variables: {},
    onData: (res) => {
      const obj = res.data.data?.onDeleteExplicitUserAccess;
      if (!obj) {
        captureSentry({
          title: 'useMeetingUserAccessForMeeting | onData | onDelete | obj is undefined',
          detail: {
            response: res.data,
          },
        });
        return;
      }
      if (obj.meetingId !== meetingId) return;
      setUserAccessList((prev) => prev.filter((item) => item.id !== obj.id));
    },
  });

  useEffect(() => {
    const list = userMeetingAccessQuery.data?.listExplicitUserAccesses.edges.map(
      (edge) => edge.node
    );
    setUserAccessList(list || []);
  }, [userMeetingAccessQuery]);

  // --------------------- return ------------------------

  return {
    isLoading: userMeetingAccessQuery.loading,
    userAccessList,
    giveUserAccess,
    deleteAccess,
  };
};

export default useMeetingUserAccessForMeeting;
